import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { carUrl } from '../lib/slugId';
import { PageHeader } from '../components/PageHeader';
import type { CarContributor, ContributionStatus } from '../api/types';
import { CarCardMedia } from '../components/CarCardMedia';

type StatusGroup = { status: ContributionStatus; items: CarContributor[] };

const STATUS_ORDER: ContributionStatus[] = ['approved', 'pending', 'denied'];

export function MyContributions() {
  const { t } = useTranslation();
  const [contributions, setContributions] = useState<CarContributor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ data: CarContributor[] }>('/api/v1/contributions')
      .then((res) => setContributions(res.data))
      .finally(() => setLoading(false));
  }, []);

  const groups: StatusGroup[] = useMemo(() => {
    return STATUS_ORDER.map((status) => ({
      status,
      items: contributions.filter((c) => c.status === status),
    })).filter((g) => g.items.length > 0);
  }, [contributions]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader crumbs={[{ label: t('myContributions.title') }]} />
      <Stack spacing={4}>
        <Typography variant="h4">{t('myContributions.title')}</Typography>

        {contributions.length === 0 ? (
          <Typography color="text.secondary">{t('myContributions.empty')}</Typography>
        ) : (
          groups.map((group) => (
            <Stack key={group.status} spacing={2}>
              <Typography variant="overline" color="text.secondary">
                {t(`myContributions.${group.status}Section`)}
              </Typography>
              <Grid container spacing={3}>
                {group.items.map((c) =>
                  c.car ? (
                    <Grid key={c.id} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Card>
                        <CardActionArea component={RouterLink} to={carUrl(c.car)}>
                          <CarCardMedia car={c.car} height={180} />
                          <CardContent>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              spacing={1}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h6" noWrap>
                                  {c.car.display_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {c.car.year} {c.car.make} {c.car.model}
                                </Typography>
                                {c.car.user && (
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {t('myContributions.ownedBy', {
                                      owner: c.car.user.name,
                                    })}
                                  </Typography>
                                )}
                              </Box>
                              <Chip
                                size="small"
                                color={
                                  c.status === 'approved'
                                    ? 'success'
                                    : c.status === 'pending'
                                    ? 'warning'
                                    : 'default'
                                }
                                label={t(`contribute.status${capitalize(c.status)}`)}
                              />
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ) : null
                )}
              </Grid>
            </Stack>
          ))
        )}
      </Stack>
    </Container>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
